// Animation variants for Framer Motion
// Includes mobile-optimized variants with reduced complexity for better performance

// =============================================================================
// UTILITY FUNCTIONS
// =============================================================================

/**
 * Check if user prefers reduced motion (for SSR safety, returns false on server)
 */
export const prefersReducedMotion = () => {
  if (typeof window === "undefined") return false
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches
}

/**
 * Check if device is mobile based on viewport width
 */
export const isMobileViewport = () => {
  if (typeof window === "undefined") return false
  return window.innerWidth < 768
}

/**
 * Get appropriate animation variant based on device and user preference
 * Returns simplified variant for mobile or when reduced motion is preferred
 */
export const getResponsiveVariant = (fullVariant, simpleVariant) => {
  if (prefersReducedMotion()) {
    return simpleVariant || {
      hidden: { opacity: 0 },
      visible: { opacity: 1, transition: { duration: 0.01 } },
    }
  }
  if (isMobileViewport()) {
    return simpleVariant || fullVariant
  }
  return fullVariant
}

// =============================================================================
// REDUCED MOTION VARIANTS (instant transitions)
// =============================================================================

export const reducedMotionVariant = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { duration: 0.01 } },
}

// =============================================================================
// MOBILE-OPTIMIZED VARIANTS (simpler animations for better performance)
// =============================================================================

export const mobileContainerVariant = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      duration: 0.3,
      staggerChildren: 0.05,
    },
  },
}

export const mobileItemVariant = {
  hidden: { opacity: 0, y: 10 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.3,
      ease: "easeOut",
    },
  },
}

export const mobileFadeInVariant = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      duration: 0.3,
      ease: "easeOut",
    },
  },
}

// =============================================================================
// DESKTOP/FULL ANIMATION VARIANTS
// =============================================================================

// Container with staggered children
export const containerVariant = {
  hidden: { opacity: 0, y: 50 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      type: "spring",
      stiffness: 100,
      damping: 20,
      staggerChildren: 0.1,
      delayChildren: 0.1,
    },
  },
}

// Standard item within a container
export const itemVariant = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      type: "spring",
      stiffness: 100,
      damping: 15,
      duration: 0.5,
    },
  },
}

// Item variant with fade in up (alias for consistency)
export const itemVariantFadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      type: "spring",
      stiffness: 100,
      damping: 15,
      duration: 0.5,
    },
  },
}

// Fade in from left
export const fadeInLeftVariant = {
  hidden: { opacity: 0, x: -50 },
  visible: {
    opacity: 1,
    x: 0,
    transition: {
      type: "spring",
      stiffness: 100,
      damping: 15,
      duration: 0.6,
    },
  },
}

// Fade in from right
export const fadeInRightVariant = {
  hidden: { opacity: 0, x: 50 },
  visible: {
    opacity: 1,
    x: 0,
    transition: {
      type: "spring",
      stiffness: 100,
      damping: 15,
      duration: 0.6,
    },
  },
}

// Fade in from bottom
export const fadeInUpVariant = {
  hidden: { opacity: 0, y: 50 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      type: "spring",
      stiffness: 100,
      damping: 15,
      duration: 0.6,
    },
  },
}

// Scale in
export const scaleInVariant = {
  hidden: { opacity: 0, scale: 0.8 },
  visible: {
    opacity: 1,
    scale: 1,
    transition: {
      type: "spring",
      stiffness: 100,
      damping: 15,
      duration: 0.5,
    },
  },
}

// =============================================================================
// HOVER EFFECTS
// =============================================================================

// Subtle hover scale for interactive elements
export const hoverScale = {
  whileHover: {
    scale: 1.03,
    transition: {
      duration: 0.3,
      ease: "easeOut",
    },
  },
}

// Button hover effect with lift
export const buttonHoverEffect = {
  whileHover: {
    scale: 1.05,
    y: -2,
    transition: {
      duration: 0.2,
      ease: "easeOut",
    },
  },
  whileTap: {
    scale: 0.98,
    transition: {
      duration: 0.1,
    },
  },
}

// Mobile-friendly hover (reduced scale for touch)
export const mobileButtonHoverEffect = {
  whileTap: {
    scale: 0.98,
    transition: {
      duration: 0.1,
    },
  },
}

// =============================================================================
// STAGGERED LIST ANIMATIONS
// =============================================================================

export const staggerListVariant = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
      delayChildren: 0.2,
    },
  },
}

export const staggerItemVariant = {
  hidden: { opacity: 0, x: -20 },
  visible: {
    opacity: 1,
    x: 0,
    transition: {
      type: "spring",
      stiffness: 100,
      damping: 15,
    },
  },
}

// Mobile-optimized stagger (faster, less movement)
export const mobileStaggerListVariant = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.05,
      delayChildren: 0.1,
    },
  },
}

export const mobileStaggerItemVariant = {
  hidden: { opacity: 0, x: -10 },
  visible: {
    opacity: 1,
    x: 0,
    transition: {
      duration: 0.2,
      ease: "easeOut",
    },
  },
}

// =============================================================================
// DECORATIVE ANIMATIONS (disabled on mobile for performance)
// =============================================================================

// Floating animation for decorative elements
export const floatingAnimation = {
  initial: { y: 0 },
  animate: {
    y: [0, -10, 0],
    transition: {
      duration: 3,
      ease: "easeInOut",
      repeat: Infinity,
      repeatType: "reverse",
    },
  },
}

// Pulse animation for attention-grabbing elements
export const pulseAnimation = {
  initial: { scale: 1 },
  animate: {
    scale: [1, 1.05, 1],
    transition: {
      duration: 2,
      ease: "easeInOut",
      repeat: Infinity,
      repeatType: "reverse",
    },
  },
}

// Subtle rotation animation
export const rotateAnimation = {
  initial: { rotate: 0 },
  animate: {
    rotate: [0, 5, 0, -5, 0],
    transition: {
      duration: 5,
      ease: "easeInOut",
      repeat: Infinity,
      repeatType: "reverse",
    },
  },
}

// Static variant for mobile (no continuous animation)
export const staticAnimation = {
  initial: {},
  animate: {},
}

// =============================================================================
// PAGE TRANSITION ANIMATIONS
// =============================================================================

export const pageTransitionVariant = {
  initial: { opacity: 0, y: 20 },
  animate: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.4,
      ease: "easeOut",
    },
  },
  exit: {
    opacity: 0,
    y: -20,
    transition: {
      duration: 0.3,
      ease: "easeIn",
    },
  },
}

// =============================================================================
// RESPONSIVE ANIMATION HOOK HELPER
// =============================================================================

/**
 * Returns appropriate animation variants based on viewport and preferences
 * Usage: const variants = useResponsiveAnimation(fullVariant, mobileVariant)
 */
export const getAnimationConfig = (options = {}) => {
  const {
    enableOnMobile = true,
    reducedIntensity = false,
  } = options

  // Check for reduced motion preference
  if (prefersReducedMotion()) {
    return {
      variants: reducedMotionVariant,
      shouldAnimate: false,
    }
  }

  // Check for mobile viewport
  if (isMobileViewport() && !enableOnMobile) {
    return {
      variants: reducedMotionVariant,
      shouldAnimate: false,
    }
  }

  if (isMobileViewport() && reducedIntensity) {
    return {
      variants: mobileItemVariant,
      shouldAnimate: true,
    }
  }

  return {
    variants: itemVariant,
    shouldAnimate: true,
  }
}

export default {
  // Utility functions
  prefersReducedMotion,
  isMobileViewport,
  getResponsiveVariant,
  getAnimationConfig,
  // Variants
  containerVariant,
  itemVariant,
  itemVariantFadeInUp,
  fadeInLeftVariant,
  fadeInRightVariant,
  fadeInUpVariant,
  scaleInVariant,
  hoverScale,
  buttonHoverEffect,
  mobileButtonHoverEffect,
  staggerListVariant,
  staggerItemVariant,
  floatingAnimation,
  pulseAnimation,
  rotateAnimation,
  pageTransitionVariant,
  // Mobile variants
  mobileContainerVariant,
  mobileItemVariant,
  mobileFadeInVariant,
  mobileStaggerListVariant,
  mobileStaggerItemVariant,
  reducedMotionVariant,
  staticAnimation,
}
